import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { AppHeader } from "../../components/AppHeader";
import { useAuth } from "../../context/AuthContext";
import { monthName } from "../../lib/dates";
import { User, Phone, Mail, LogOut, ShieldCheck } from "lucide-react";
import { StarsInline, QualityRing } from "../../components/StarRating";
import { PinSetupCard } from "../../components/PinSetupCard";
import { toast } from "sonner";

const now = new Date();

function CredentialCard({ title, target, currentEmail, onSaved }) {
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const input = "w-full h-11 px-4 rounded-xl border border-slate-300 focus:border-[#C86D51] outline-none text-base bg-white";

  const save = async () => {
    if (!newEmail && !newPassword) { toast.error("Enter a new email or a new password to save."); return; }
    if (newPassword && newPassword !== confirm) { toast.error("New password and confirm password do not match."); return; }
    setSaving(true);
    try {
      const body = {};
      if (newEmail) body.new_email = newEmail;
      if (newPassword) { body.new_password = newPassword; body.confirm_password = confirm; }
      await api.put(`/account/credentials/${target}`, body);
      toast.success(`${title} updated`);
      setNewEmail(""); setNewPassword(""); setConfirm("");
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid={`cred-card-${target}`} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
      <h3 className="font-display text-base font-bold text-slate-900">{title}</h3>
      <div>
        <label className="text-xs font-bold uppercase text-slate-400">Current Email</label>
        <p data-testid={`cred-current-${target}`} className="font-semibold text-slate-700 mt-0.5">{currentEmail || "—"}</p>
      </div>
      <div>
        <label className="text-sm font-semibold text-slate-700 mb-1 block">New Email</label>
        <input data-testid={`cred-new-email-${target}`} type="email" className={input} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="new@email.com" autoComplete="off" />
      </div>
      <div>
        <label className="text-sm font-semibold text-slate-700 mb-1 block">New Password</label>
        <input data-testid={`cred-new-password-${target}`} type="password" className={input} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
      </div>
      <div>
        <label className="text-sm font-semibold text-slate-700 mb-1 block">Confirm Password</label>
        <input data-testid={`cred-confirm-password-${target}`} type="password" className={input} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
      </div>
      <button data-testid={`cred-save-${target}`} onClick={save} disabled={saving}
        className="w-full py-3 rounded-xl copper-gradient text-white font-bold shadow-md active:scale-[0.98] transition-transform disabled:opacity-60">
        {saving ? "Saving…" : "Save Changes"}
      </button>
    </div>
  );
}

export default function Profile() {
  const { user, logout } = useAuth();
  const [score, setScore] = useState(null);
  const [creds, setCreds] = useState(null);

  const loadCreds = () => api.get("/account/credentials").then((r) => setCreds(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/reports/manager", { params: { year: now.getFullYear(), month: now.getMonth() + 1 } }).then((r) => setScore(r.data)).catch(() => {});
    loadCreds();
  }, []);

  return (
    <>
      <AppHeader title="Profile" subtitle="Manager" />
      <div className="px-4 lg:px-8 py-4 space-y-6 lg:max-w-3xl lg:mx-auto">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center gap-4">
          <div className="h-16 w-16 rounded-2xl copper-gradient flex items-center justify-center text-white"><User className="h-8 w-8" /></div>
          <div>
            <h2 className="font-display text-xl font-black text-slate-900">{user?.name}</h2>
            <p className="text-sm text-slate-500 flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {user?.email}</p>
            {user?.phone && <p className="text-sm text-slate-500 flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {user.phone}</p>}
          </div>
        </div>

        {score && (
          <section data-testid="manager-quality-card" className="bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="font-display text-base font-bold mb-1">My Quality Score</h2>
            <p className="text-xs text-slate-400 mb-4 uppercase font-bold">{monthName(now.getMonth() + 1)} {now.getFullYear()}</p>
            <div className="flex items-center gap-5">
              <QualityRing pct={score.quality_pct} avg={score.avg_rating} />
              <div className="flex-1 space-y-2">
                {score.avg_rating != null ? (
                  <>
                    <div className="flex items-center gap-2"><StarsInline value={score.avg_rating} size="h-5 w-5" /><span className="tnum font-black text-amber-600">{score.avg_rating} / 5</span></div>
                    <p className="text-sm text-slate-600">Jobs quality rated: <b className="tnum">{score.jobs_rated}</b></p>
                    <p className="text-sm text-slate-600">Quality score: <b className="tnum text-amber-600">{score.quality_pct}%</b></p>
                  </>
                ) : (
                  <p className="text-sm text-slate-400 font-medium">No quality ratings yet</p>
                )}
              </div>
            </div>
          </section>
        )}

        {score && (
          <section className="bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="font-display text-base font-bold mb-1">My Performance</h2>
            <p className="text-xs text-slate-400 mb-4 uppercase font-bold">{monthName(now.getMonth() + 1)} {now.getFullYear()}</p>
            <div className="grid grid-cols-3 gap-2 text-center mb-4">
              <div><p className="tnum text-2xl font-black text-slate-900">{score.completed}</p><p className="text-[9px] font-bold uppercase text-slate-400">Done</p></div>
              <div><p className="tnum text-2xl font-black text-emerald-600">{score.on_time}</p><p className="text-[9px] font-bold uppercase text-slate-400">On Time</p></div>
              <div><p className="tnum text-2xl font-black text-red-600">{score.breached}</p><p className="text-[9px] font-bold uppercase text-slate-400">Breached</p></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-emerald-50 py-2 text-center"><p className="tnum text-lg font-black text-emerald-700">{score.on_time_pct}%</p><p className="text-[9px] font-bold uppercase text-emerald-600">On-Time</p></div>
              <div className="rounded-xl bg-red-50 py-2 text-center"><p className="tnum text-lg font-black text-red-700">{score.breach_pct}%</p><p className="text-[9px] font-bold uppercase text-red-600">Breach</p></div>
            </div>
          </section>
        )}

        {creds && (
          <section data-testid="account-login-section">
            <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" /> Account & Login
            </h2>
            <div className="grid gap-4 lg:grid-cols-2">
              <CredentialCard title="Manager Login" target="manager" currentEmail={creds.manager?.email} onSaved={loadCreds} />
              <CredentialCard title="Owner Login" target="owner" currentEmail={creds.owner?.email} onSaved={loadCreds} />
            </div>
          </section>
        )}

        <PinSetupCard />

        <button data-testid="profile-logout-button" onClick={logout} className="w-full flex items-center justify-center gap-2 bg-white rounded-2xl border border-slate-200 p-4 font-bold text-red-600 active:scale-[0.99] transition-transform">
          <LogOut className="h-5 w-5" /> Log Out
        </button>
      </div>
    </>
  );
}
